// src/App.js
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-19
//
//  1. FIVE SIDEBAR ITEMS LED TO THE LOGIN FORM.
//       Email Sent, Servers Management, ESP Management, Data Management and
//       Calc Email Setting had no route here, so they hit the catch-all
//       (`path="*"` → `/`) and `/` renders <Login/>. Clicking any of them
//       looked exactly like being logged out.
//       They now render <ComingSoon/> — honest, keeps the sidebar and
//       header, and offers a way back. Same for "Settings" in the user
//       dropdown in Header.js.
//
//  2. THE CATCH-ALL SENT YOU TO THE LOGIN FORM TOO.
//       Any mistyped address did the same thing. It now goes to the
//       Dashboard. If you aren't logged in, ProtectedRoute bounces you to
//       `/` and you get the login form properly — which is the correct
//       outcome, reached the correct way.
//
//  3. `/` NOW KNOWS WHETHER YOU ARE LOGGED IN.
//       It used to render the login form unconditionally, even for a
//       signed-in user. It now sends a signed-in user to the Dashboard and
//       shows the form only to someone who isn't. `/login` is deliberately
//       left alone, so you can still reach the form on purpose (e.g. to
//       sign in as a different user).
//
//  NOTHING ELSE CHANGED — every existing route keeps its exact path,
//  component and permission/role guard.
// ═══════════════════════════════════════════════════════════════════════════

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";   // CHANGED: useAuth added
import ProtectedRoute    from "./components/ProtectedRoute";
import Login             from "./pages/Login";
import Dashboard         from "./pages/Dashboard";
import Users             from "./pages/Users";
import Revenue           from "./pages/Revenue";
import Affiliates        from "./pages/Affiliates";
import Offers            from "./pages/Offers";
import OfferDetail       from "./pages/OfferDetail";
import Links             from "./pages/Links";
import Domains           from "./pages/Domains";   // Phase 5.2a
import ComingSoon        from "./pages/ComingSoon"; // ADDED 2026-09-19

/* ─────────────────────────────────────────────────────────────────────────
   ADDED 2026-09-19 — what "/" should do.

   Before: `<Route path="/" element={<Login />} />` — the login form, always,
   even if you were already signed in. That is why a mistyped URL, or one of
   the five dead menu items, looked like a logout.

   Now:
     • still checking who you are  → a neutral "Loading…" (NOT the login
       form, which would flash on screen and then vanish)
     • signed in                   → straight to the Dashboard
     • not signed in               → the login form, as before

   `/login` is untouched and always shows the form, so signing in as a
   different user is still possible.
   ───────────────────────────────────────────────────────────────────────── */
function RootRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: "100vh", background: "#080810", color: "#505060", fontSize: 13,
      }}>
        Loading i-Panel…
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" replace /> : <Login />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* CHANGED 2026-09-19: was element={<Login />} */}
          <Route path="/"            element={<RootRoute />} />
          <Route path="/login"       element={<Login />} />
          <Route path="/dashboard"   element={<ProtectedRoute><Dashboard /></ProtectedRoute>}/>
          <Route path="/revenue"     element={<ProtectedRoute permission="revenue"><Revenue /></ProtectedRoute>}/>
          <Route path="/affiliates"  element={<ProtectedRoute role="admin"><Affiliates /></ProtectedRoute>}/>
          <Route path="/users"       element={<ProtectedRoute role="admin"><Users /></ProtectedRoute>}/>
          <Route path="/offers"      element={<ProtectedRoute><Offers /></ProtectedRoute>}/>
          <Route path="/offers/:affId/:offerId" element={<ProtectedRoute><OfferDetail /></ProtectedRoute>}/>
          <Route path="/links"       element={<ProtectedRoute><Links /></ProtectedRoute>}/>
          <Route path="/domains"     element={<ProtectedRoute permission="domains"><Domains /></ProtectedRoute>}/>

          {/* ── ADDED 2026-09-19 ────────────────────────────────────────────
              The five sidebar entries (plus Settings) that previously fell
              through to the login form. The `active` value matches the id in
              components/Sidebar.js so the menu still highlights correctly.
              When a real page is built, swap <ComingSoon/> for it here and
              nothing else needs to change.
              ──────────────────────────────────────────────────────────── */}
          <Route path="/emailsent" element={<ProtectedRoute><ComingSoon title="Email Sent"          active="emailsent"/></ProtectedRoute>}/>
          <Route path="/servers"   element={<ProtectedRoute><ComingSoon title="Servers Management"  active="servers"  /></ProtectedRoute>}/>
          <Route path="/esp"       element={<ProtectedRoute><ComingSoon title="ESP Management"      active="esp"      /></ProtectedRoute>}/>
          <Route path="/data"      element={<ProtectedRoute><ComingSoon title="Data Management"     active="data"     /></ProtectedRoute>}/>
          <Route path="/tools"     element={<ProtectedRoute><ComingSoon title="Calc Email Setting"  active="tools"    /></ProtectedRoute>}/>
          <Route path="/settings"  element={<ProtectedRoute><ComingSoon title="Settings"            active={null}     /></ProtectedRoute>}/>

          {/* CHANGED 2026-09-19: was Navigate to="/" — i.e. the login form.
              An unknown address now goes to the Dashboard. A visitor who
              isn't signed in is bounced to "/" by ProtectedRoute and gets
              the login form there, which is the right result by the right
              route. */}
          <Route path="*"            element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
